import { ApolloClient, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { useToast } from '@chakra-ui/react';

import {
  url as commonUrl,
  utils as commonUtils,
} from '@finley-technologies/common';

import generatedIntrospection from 'types/dashboard-core-Introspection.generated.d';

import {
  ApolloClientParamTypes,
  baseGraphQlErrorHandler,
  baseNetworkErrorHandler,
  baseAuthMiddleware,
  baseCustomFetch,
  baseInMemoryCache,
  executionModeMiddleware,
  targetHeaderMiddleware,
} from './apollo.utils';
import { useAuthToken } from 'packages/logged-out/hooks/useAuthManagement';

const errorHandler = (
  toast: ApolloClientParamTypes['toast'],
  removeAuthToken: ApolloClientParamTypes['removeAuthToken'],
) => {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    const result = baseGraphQlErrorHandler(
      toast,
      removeAuthToken,
      { graphQLErrors, operation },
      { overrideAndSendSentryError: commonUtils.EnvUtils.isProduction() },
    );
    if (result === 'terminate') {
      return undefined;
    }

    baseNetworkErrorHandler(toast, networkError);

    return forward(operation);
  });
};

export const createApolloClient = ({
  toast,
  authToken,
  uri,
  possibleTypes,
  removeAuthToken,
  executionMode,
  targetHeaderValue,
}: ApolloClientParamTypes) => {
  const cache = baseInMemoryCache(possibleTypes);

  const httpLink = new HttpLink({ uri, fetch: baseCustomFetch });
  const link = baseAuthMiddleware(authToken)
    .concat(errorHandler(toast, removeAuthToken))
    .concat(executionModeMiddleware({ executionMode }))
    .concat(targetHeaderMiddleware({ targetHeaderValue }))
    .concat(httpLink);

  return new ApolloClient({ cache, link, credentials: 'include' });
};

export const useAppApolloClient = (params?: {
  executionMode: 'live' | 'sandbox';
  targetHeaderValue?: string;
}) => {
  // will try to pull jwt auth if available in cookies
  const [authToken, , removeAuthToken] = useAuthToken();
  const toast = useToast();
  const uri = commonUrl.getGraphqlapiUrl();

  return createApolloClient({
    toast,
    authToken,
    removeAuthToken,
    uri,
    possibleTypes: generatedIntrospection.possibleTypes,
    executionMode: params?.executionMode ?? 'live',
    targetHeaderValue: params?.targetHeaderValue,
  });
};
